/** Tenant IDs that allow browsing /posts without signing in (deployment-specific). */
export const GUEST_POSTS_TENANT_IDS =  'OpenCommunityX'
  .split(',')
  .map((s) => s.trim())
  .filter(Boolean);

export function isGuestPostsViewEnabled() {
  return GUEST_POSTS_TENANT_IDS.length > 0;
}

export const GUEST_SIGN_IN_LABEL = 'Sign in';
export const GUEST_CREATE_ACCOUNT_LABEL = 'Create account';

/** Safe in-app paths allowed after sign-in (blocks open redirects). */
export function safePostsReturnTo(raw) {
  if (!raw || typeof raw !== 'string') return '/posts';
  try {
    const path = raw.startsWith('/') ? raw : decodeURIComponent(raw);
    if (
      path.startsWith('/posts') ||
      path.startsWith('/storagebox') ||
      path.startsWith('/products') ||
      path.startsWith('/audio-player') ||
      path.startsWith('/text-editor') ||
      path.startsWith('/costing/cashflow') ||
      path.startsWith('/sm/all-messages') ||
      path.startsWith('/messages')
    ) {
      return path;
    }
    return '/posts';
  } catch {
    return '/posts';
  }
}

/** After sign-in, open the feed at the beginning (not a deep thread/club the guest may have opened). */
export function postsUrlAfterLogin(raw) {
  const safe = safePostsReturnTo(raw);
  if (
    safe.startsWith('/storagebox') ||
    safe.startsWith('/audio-player') ||
    safe.startsWith('/text-editor') ||
    safe.startsWith('/costing/cashflow') ||
    safe.startsWith('/sm/all-messages') ||
    safe.startsWith('/messages')
  ) {
    return safe;
  }
  try {
    const qIdx = safe.indexOf('?');
    const path = qIdx >= 0 ? safe.slice(0, qIdx) : safe;
    const params = new URLSearchParams(qIdx >= 0 ? safe.slice(qIdx + 1) : '');
    const view = params.get('view') || 'discussions';
    const deepViews = new Set(['discussion', 'discussion-club', 'discussions-moderation', 'manage-profile']);
    if (deepViews.has(view)) {
      params.set('view', 'discussions');
      params.delete('d');
      params.delete('club');
      params.delete('story');
      if (!params.get('sort')) params.set('sort', 'recommended');
    }
    const qs = params.toString();
    return `${path}${qs ? `?${qs}` : ''}`;
  } catch {
    return '/posts?view=discussions&sort=recommended';
  }
}

export function isGuestAuthError(err) {
  const status = err?.status;
  const msg = String(err?.message || err?.data?.message || '').toLowerCase();
  return status === 401 || msg.includes('unauthorized') || msg.includes('token not found');
}

export function isEmailNotVerifiedError(err) {
  if (err?.data?.code === 'EMAIL_NOT_VERIFIED') return true;
  const msg = String(err?.message || err?.data?.message || '').toLowerCase();
  return msg.includes('verify your email');
}

export const PENDING_VERIFICATION_EMAIL_KEY = 'bahlus_pending_verification_email';

export function stashPendingVerificationEmail(email) {
  const value = String(email || '').trim();
  if (!value || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(PENDING_VERIFICATION_EMAIL_KEY, value);
  } catch {
    /* ignore */
  }
}

export function consumePendingVerificationEmail() {
  if (typeof sessionStorage === 'undefined') return '';
  try {
    const value = String(sessionStorage.getItem(PENDING_VERIFICATION_EMAIL_KEY) || '').trim();
    if (value) sessionStorage.removeItem(PENDING_VERIFICATION_EMAIL_KEY);
    return value;
  } catch {
    return '';
  }
}

export function buildSigninUrl({ mode = 'signin', returnTo, reauth = false } = {}) {
  const params = new URLSearchParams();
  if (mode === 'signup') params.set('mode', 'signup');
  if (reauth) params.set('reauth', '1');
  const rt =
    returnTo ||
    (typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : '/posts');
  params.set('returnTo', safePostsReturnTo(rt));
  return `/signin?${params.toString()}`;
}
